import { Suspense, lazy } from "react";
import type { ComponentType } from "react";
import { Route, Routes } from "react-router-dom";
import { AuthProvider, RequireAudience } from "./auth";
import LoginScreen from "./auth/LoginScreen";
import ChangePasswordScreen from "./auth/ChangePasswordScreen";

/**
 * RocketRep for Aspira — one SPA, two halves on one origin.
 *
 *   /              the MIS and admin panel  (src/mis/)
 *   /pro           the PRO PWA              (src/pro/ProApp.tsx)
 *   /login         RocketRep's own sign-in  (src/auth/)
 *   /change-password
 *
 * Both product halves are lazy, so the entry chunk is the router and the login
 * screen and nothing else: a rep opening /pro on a phone never downloads the
 * MIS rollups, and a sales head opening / never downloads the field app.
 *
 * ── Why /pro is mounted the way it is ─────────────────────────────────────
 * `src/pro/` is being written in parallel. A bare `import("./pro/ProApp")`
 * would make this file fail to resolve — build and typecheck both — during
 * every minute that file does not exist. `import.meta.glob` resolves at build
 * time to whatever is on disk: the module when it is there, an empty map when
 * it is not. So the MIS half always builds, and /pro lights up the moment its
 * builder lands the file. No import of theirs appears in my type graph.
 *
 * ── Why the guards wrap the Suspense and not the other way round ──────────
 * RequireAudience answers before a chunk is requested, so somebody who may not
 * open a half never downloads it — and, more to the point, the login screen is
 * never held behind a lazy chunk that has to load before it can say "sign in".
 *
 * Authentication is RocketRep's own (Edward, 2026-09-16): its own accounts, its
 * own password hashes, its own session cookie. Nothing here touches LaaS.
 */
/* ★ ProAppLive, not ProApp. ProApp takes its day as a prop and defaults to the
   fixture, which is exactly right for the screenshot harness and the render
   check; ProAppLive is the one that loads the real warehouse and owns the
   loading, empty and failure states a demo never has. Mounting ProApp here
   would serve a rep the demo cast. */
const proModules = import.meta.glob("./pro/ProAppLive.tsx");
const PRO_KEY = "./pro/ProAppLive.tsx";

function ProNotLanded() {
  return (
    <div className="rr-boot">
      <div className="rr-boot-card">
        <div className="rr-boot-k">The PRO app</div>
        <p>
          <code>src/pro/ProAppLive.tsx</code> is not in this build. The PRO PWA is a
          separate module on the same origin; when it is present this route mounts
          it, and until then it says so rather than showing a blank screen.
        </p>
        <p>
          The MIS half is at <a href="/">aspira.rocketrep.pro/</a>.
        </p>
      </div>
    </div>
  );
}

const ProApp = lazy(async () => {
  const load = proModules[PRO_KEY];
  if (!load) return { default: ProNotLanded };
  try {
    const mod = (await load()) as { default?: ComponentType };
    return { default: mod.default ?? ProNotLanded };
  } catch {
    // Their module threw on load. That is their bug to fix, not a white screen
    // for whoever opened the MIS in the next tab.
    return { default: ProNotLanded };
  }
});

const MisRoutes = lazy(() => import("./mis/MisRoutes"));

/** Held for the moment a chunk is in flight: the wordmark and a spinner,
    faded in after 150 ms so a fast load never flashes it. Styles in auth.css,
    which the eager login chrome already loads. */
const Held = (
  <div className="rr-boot" aria-busy="true">
    <div className="rr-held">
      <img src="/brand/rr-wordmark-dark.png" alt="RocketRep" />
      <span className="rr-held-spin" aria-hidden="true" />
      <span className="rr-held-t">Loading…</span>
    </div>
  </div>
);

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* The two credential screens are eager. They are the first thing a
            signed-out person sees, and putting a network round trip in front
            of "sign in" is how a login screen ends up blank on a bad line. */}
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/change-password" element={<ChangePasswordScreen />} />

        <Route
          path="/pro/*"
          element={
            <RequireAudience audience="pro">
              <Suspense fallback={Held}>{<ProApp />}</Suspense>
            </RequireAudience>
          }
        />
        <Route
          path="/*"
          element={
            <RequireAudience audience="mis">
              <Suspense fallback={Held}>{<MisRoutes />}</Suspense>
            </RequireAudience>
          }
        />
      </Routes>
    </AuthProvider>
  );
}
